<script setup lang="ts">
import { User, Lock, LogOut } from "@iconoir/vue";
import ProfileModal from "~/views/profile/ProfileModal.vue";
import ChangePasswordModal from "~/views/profile/ChangePasswordModal.vue";

const { t } = useI18n();
const { $api } = useNuxtApp();
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const profileModalRef = ref<InstanceType<typeof ProfileModal> | null>(null);
const passwordModalRef = ref<InstanceType<typeof ChangePasswordModal> | null>(null);

const initials = computed(() => {
  const source = user.value?.fullName || user.value?.email || "";
  return source.substring(0, 2).toUpperCase();
});

const role = computed(() =>
  user.value?.role?.name || user.value?.roleKey || ""
);

function onUpdateProfile() {
  profileModalRef.value?.open();
}

function onChangePassword() {
  passwordModalRef.value?.open();
}

async function onLogout() {
  // Revoke the token server-side; clearing local state alone leaves it valid.
  try {
    await $api("/logout", { method: "POST" });
  } catch {
    // Already invalid or unreachable — sign out locally either way.
  }
  authStore.logout();
  navigateTo("/login");
}
</script>

<template>
  <Popover size="sm">
    <template #trigger>
      <div class="user-menu-trigger">
        <Avatar :src="user?.imageAsset?.url" :initials="initials" size="medium" shape="rounded" />
      </div>
    </template>

    <div class="flex flex-col gap-xsmall padding-small">
      <div class="flex flex-col gap-2xsmall">
        <div class="flex items-center gap-xsmall">
          <span class="text-body-tertiary content-neutral-strong">
            {{ user?.fullName || t("user.unknown") }}
          </span>
        </div>
        <span v-if="user?.email" class="text-body-small-secondary content-neutral-medium">
          {{ role }} &nbsp;|&nbsp; {{ user.email }}
        </span>
      </div>

      <Divider />

      <PopoverItem :icon="User" :label="t('user.updateProfile')" @click="onUpdateProfile" />
      <PopoverItem :icon="Lock" :label="t('user.changePassword')" @click="onChangePassword" />

      <Divider />

      <PopoverItem :icon="LogOut" :label="t('user.logout')" destructive @click="onLogout" />
    </div>
  </Popover>

  <ProfileModal ref="profileModalRef" />
  <ChangePasswordModal ref="passwordModalRef" />
</template>

<style scoped lang="scss">
.user-menu-trigger {
  cursor: pointer;
  border-radius: token("corner-radius-rounded");
  transition: box-shadow token("motion-duration-fast") token("motion-ease-standard");

  &:hover {
    box-shadow: 0 0 0 2.5px var(--color-bg-fill-glass-hover);
    background: var(--color-bg-fill-glass-hover);
  }
}
</style>
